import { Add, Delete, WarningAmberOutlined } from '@mui/icons-material'
import { Box, Button, FormControl, MenuItem, Select, Skeleton, TextField, Tooltip } from '@mui/material'
import { AxiosError } from 'axios'
import IconButton from 'components/icon-button'
import Loading from 'components/loading'
import { useAlert } from 'contexts/alert'
import { useProject } from 'contexts/project'
import dayjs from 'dayjs'
import { FormEvent, Fragment, useEffect, useState } from 'react'
import api from 'services/api'
import { cpfMask, removeExpecialCharactersFromCPF } from 'utils/cpf'
import { useStyles } from './styles'

interface Item {
  id: string
  cpf: string
  name: string
  birthday: string
  type: string
  cpfHolder: string
  plan: string
  validityOfExclusion: string
  state: 'draft' | 'current'
  hasDeleted: boolean
}

interface PlanParams {
  id: string
  planName: string
}

const BaseElegivelExcecoes = () => {
  const classes = useStyles()
  const { openAlert } = useAlert()
  const { data: project } = useProject()

  const [items, setItems] = useState<Item[]>([])
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false)
  const [isIndexLoading, setIsIndexLoading] = useState<boolean>(false)
  const [loading, setLoading] = useState<Record<number, boolean>>({})
  const [duplicate, setDuplicate] = useState<Record<number, boolean>>({})
  const [plans, setPlans] = useState<PlanParams[]>([])

  const handleAddItem = () => {
    const newItem: Item = {
      id: '',
      cpf: '',
      name: '',
      birthday: '',
      type: '',
      cpfHolder: '',
      plan: '',
      validityOfExclusion: '',
      state: 'draft',
      hasDeleted: false,
    }

    setItems([...items, newItem])
  }

  const handleClean = async () => await handleGetIndexed()

  const handleDeleteItem = (index: number) => {
    const updatedItems = [...items]
    const item = updatedItems[index]

    if (item.state === 'current') {
      updatedItems[index].hasDeleted = true
    } else {
      updatedItems.splice(index, 1)
    }

    setItems(updatedItems)
  }

  const handleChangeCPF = async (value: string, index: number) => {
    value = removeExpecialCharactersFromCPF(value)

    const newItems = [...items]
    newItems[index].cpf = value
    setItems(newItems)

    const itemsForSearch = [...items]

    itemsForSearch.splice(index, 1)

    const isActualDuplicate = !!itemsForSearch.find((item) => item.cpf === value)

    setDuplicate((prev) => ({
      ...prev,
      [index]: isActualDuplicate,
    }))

    if (isActualDuplicate) {
      return
    }

    if (value.length === 11) {
      try {
        setLoading((prev) => ({ ...prev, [index]: true }))

        const body = {
          projectId: project.id,
          taxPayerId: value,
        }

        const { data } = await api.post('/apps/excecao/find-by-tax-payer-id', body)

        const person = Array.isArray(data.data) ? data.data[0] : data.data
        if (!person) return

        const updatedItems = [...items]

        updatedItems[index] = {
          ...updatedItems[index],
          name: person.name,
          birthday: person.birthDate,
          type: person.benefitType,
          cpfHolder: person.mainTaxPayerId,
        }

        setItems(updatedItems)
      } catch (error) {
        if (error instanceof AxiosError) {
          openAlert(error.response?.data.data.message, 'error')
        }
      } finally {
        setLoading((prev) => ({ ...prev, [index]: false }))
      }
    }
  }

  const handleGetPlans = async () => {
    try {
      const { data } = await api.get(`/apps/excecao/list-plans/${project.id}`)
      setPlans(data.data)
    } catch (error) {
      if (error instanceof AxiosError) {
        openAlert(error.response?.data.data.message, 'error')
      }
    }
  }

  const handleGetIndexed = async () => {
    try {
      setIsIndexLoading(true)

      const { data } = await api.post('/apps/excecao/list', {
        projectId: project.id,
      })

      const content = data.data.map((item: any) => ({
        id: item.id,
        cpf: removeExpecialCharactersFromCPF(item.taxPayerId),
        name: item.name,
        birthday: item.birthDate,
        type: item.benefitType,
        cpfHolder: item.mainTaxPayerId ?? '',
        plan: item.plan,
        validityOfExclusion: item.expireDate ? dayjs(item.expireDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
        state: 'current',
        hasDeleted: item.disabled ?? false,
      }))

      setItems(content)
    } catch (error) {
      if (error instanceof AxiosError) {
        openAlert(error.response?.data.data.message, 'error')
      }
    } finally {
      setIsIndexLoading(false)
    }
  }

  const onSubmit = async (e: FormEvent) => {
    try {
      e.preventDefault()

      if (items.some((item) => item.name === '' || item.plan === '')) {
        return
      }

      setIsSaveDisabled(true)

      const content = items.map((item) => ({
        taxPayerId: removeExpecialCharactersFromCPF(item.cpf),
        planId: item.plan,
        ...(item.validityOfExclusion != '' && { expireDate: dayjs(item.validityOfExclusion).format('DD/MM/YYYY') }),
        ...(item.hasDeleted && { disabled: true }),
        ...(item.id && { id: item.id }),
      }))

      const body = {
        projectId: project.id,
        excecoes: content,
      }

      await api.post('/apps/excecao', body)
      await handleGetIndexed()

      openAlert('Adesões salvas com sucesso', 'success')
    } catch (error) {
      if (error instanceof AxiosError) {
        openAlert(error.response?.data.data.message, 'error')
      }
    } finally {
      setIsSaveDisabled(false)
    }
  }

  const onChangeOutlineInInput = (value: string, index: number) => {
    value = removeExpecialCharactersFromCPF(value)

    if (value.length <= 0) {
      return '2px solid #3024E1'
    }

    if (duplicate[index]) {
      return '2px solid #FF0000'
    }

    return 'none'
  }

  useEffect(() => {
    handleGetPlans()
    handleGetIndexed()
  }, [])

  //validations to actions
  const itemsIsPresent = items.length > 0
  const haveIncompleteItems = !!items.find((item) => item.name === '' || item.plan === '')
  const haveDeletedItems = !!items.find((item) => item.hasDeleted)

  const isActionDisabled =
    !itemsIsPresent ||
    (haveIncompleteItems && haveDeletedItems) ||
    (!haveIncompleteItems && !haveDeletedItems && items.every((item) => item.state === 'current'))

  return (
    <Box
      component={'form'}
      className={classes.root}
      onSubmit={onSubmit}
    >
      {isIndexLoading ? (
        <Box className={classes.initialBase}>
          <Loading />
        </Box>
      ) : (
        <Fragment>
          {!isActionDisabled && (
            <Box className={classes.alertBox}>
              <WarningAmberOutlined
                color='warning'
                fontSize='small'
              />
              <Box className={classes.alertText}>
                <strong>Você tem modificações que não foram salvas</strong>
                <span>Clique em Salvar para não perder as modificações.</span>
              </Box>
            </Box>
          )}

          <Box className={classes.addButton}>
            <Tooltip title='Adicionar'>
              <Add onClick={handleAddItem} />
            </Tooltip>
          </Box>

          {items.length <= 0 || items.every((items) => items.hasDeleted) ? (
            <Box className={classes.initialBase}>
              <span className={classes.initialBaseText}>Não há dados disponíveis, clique no botão</span>
              <Add />
              <span className={classes.initialBaseText}>para adicionar uma nova exceção</span>
            </Box>
          ) : (
            <table className={classes.tableBase}>
              <thead className={classes.tableHeader}>
                <tr>
                  <th className={classes.tableOthersSize}>CPF</th>
                  <th className={classes.tableOthersSize}>Nome</th>
                  <th className={classes.tableOthersSize}>Data de nascimento</th>
                  <th className={classes.tableOthersSize}>Tipo</th>
                  <th className={classes.tableOthersSize}>Vinculado ao CPF</th>
                  <th className={classes.tableOthersSize}>Plano</th>
                  <th className={classes.tableOthersSize}>Vigência da exclusão (opcional)</th>
                  <th className={classes.tableActionSize}></th>
                </tr>
              </thead>

              <tbody className={classes.tableBody}>
                {items.map((item, index) => {
                  if (item.hasDeleted) {
                    return null
                  }

                  return (
                    <tr key={item.id || index}>
                      <td className={classes.tableOthersSize}>
                        <input
                          value={cpfMask(item.cpf)}
                          className={classes.searchInput}
                          style={{ outline: onChangeOutlineInInput(item.cpf, index) }}
                          onChange={async (e) => await handleChangeCPF(e.target.value, index)}
                          placeholder={duplicate[index] ? 'CPF na exceção' : 'Digite o CPF aqui'}
                        />
                        {duplicate[index] && <div className={classes.hint}>CPF na exceção</div>}
                      </td>

                      <td className={classes.tableOthersSize}>
                        {loading[index] ? (
                          <Skeleton
                            variant='rounded'
                            width={140}
                            height={20}
                          />
                        ) : (
                          <input
                            value={item.name}
                            readOnly
                            className={classes.searchInput}
                          />
                        )}
                      </td>

                      <td className={classes.tableOthersSize}>
                        {loading[index] ? (
                          <Skeleton
                            variant='rounded'
                            width={140}
                            height={20}
                          />
                        ) : (
                          <input
                            value={item.birthday}
                            readOnly
                            className={classes.searchInput}
                          />
                        )}
                      </td>

                      <td className={classes.tableOthersSize}>
                        {loading[index] ? (
                          <Skeleton
                            variant='rounded'
                            width={140}
                            height={20}
                          />
                        ) : (
                          <input
                            value={item.type}
                            readOnly
                            className={classes.searchInput}
                          />
                        )}
                      </td>

                      <td className={classes.tableOthersSize}>
                        <input
                          value={item.cpfHolder}
                          readOnly
                          className={classes.searchInput}
                        />
                      </td>

                      <td className={classes.tableOthersSize}>
                        <FormControl
                          variant='standard'
                          sx={{ m: 1, minWidth: 120 }}
                        >
                          <Select
                            value={item.plan}
                            onChange={(e) => {
                              const newItems = [...items]
                              newItems[index].plan = e.target.value
                              setItems(newItems)
                            }}
                            label='Plano'
                            placeholder='Plano'
                            className={classes.inputDate}
                          >
                            {plans.map(({ id, planName }) => (
                              <MenuItem
                                key={id}
                                value={id}
                              >
                                {planName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </td>

                      <td className={classes.tableOthersSize}>
                        <TextField
                          type='date'
                          InputLabelProps={{ shrink: true }}
                          variant='filled'
                          fullWidth
                          margin='normal'
                          value={item.validityOfExclusion}
                          onChange={(e) => {
                            const newItems = [...items]
                            newItems[index].validityOfExclusion = e.target.value
                            setItems(newItems)
                          }}
                          className={classes.inputDate}
                        />
                      </td>

                      <td className={classes.tableActionSize}>
                        <IconButton
                          icon={Delete}
                          isWarning
                          onClick={() => handleDeleteItem(index)}
                          tooltip='Remover Exceção'
                          isDisabled={isSaveDisabled}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          <Box className={classes.actions}>
            <Button
              type='button'
              variant='outlined'
              disabled={isActionDisabled || isSaveDisabled}
              className={classes.cancelButton}
              onClick={handleClean}
            >
              Cancelar
            </Button>
            <Button
              type='submit'
              variant='contained'
              size='small'
              disabled={isActionDisabled || isSaveDisabled}
              className={classes.submitButton}
            >
              Salvar
            </Button>
          </Box>
        </Fragment>
      )}
    </Box>
  )
}

export default BaseElegivelExcecoes
